/**
 * Markdown-it plugin for handling citation references
 *
 * Supports three formats:
 * 1. Inline with URL: [^1](https://example.com "Optional Title")
 * 2. Simple reference: [^1] or [^Note]
 * 3. Numeric only: [1] with citations object
 *
 * Converts to clickable/hoverable superscript elements
 */

const NUMERIC_CITATION_REGEX = /\[(\d+)\]/g;
const SIMPLE_REFERENCE_REGEX = /\[\^([^\]]+)\]/g;

// Helper function to escape HTML
function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

export default function citationPlugin(md, citations = {}) {
  // Store citations in the markdown instance for later use
  md.citations = citations;

  // Add inline rule to catch [^N](url "title") BEFORE link parser
  md.inline.ruler.before('link', 'citation_inline', (state, silent) => {
    const start = state.pos;
    const max = state.posMax;

    // Must start with [
    if (state.src.charCodeAt(start) !== 0x5b /* [ */) {
      return false;
    }

    // Must be [^
    if (start + 1 >= max || state.src.charCodeAt(start + 1) !== 0x5e /* ^ */) {
      return false;
    }

    // Try to match [^N](url "title") or [^N](url)
    // More permissive regex that handles URLs with special characters
    const remainingText = state.src.slice(start);
    const match = remainingText.match(
      /^\[\^([^\]]+)\]\(([^)"]+)(?:\s+"([^"]+)")?\)/
    );

    if (!match) {
      return false;
    }

    if (silent) {
      return true;
    }

    const fullMatch = match[0];
    const citationKey = match[1].trim();
    let url = match[2].trim();
    const title = match[3] ? match[3].trim() : url;

    // Remove angle brackets if present <url>
    if (url.startsWith('<') && url.endsWith('>')) {
      url = url.slice(1, -1);
    }

    // Store citation
    if (!md.citations[citationKey]) {
      md.citations[citationKey] = { url, title };
    }

    // Move position
    state.pos += fullMatch.length;

    // Create HTML inline token with proper structure
    const token = state.push('html_inline', '', 0);
    token.content = [
      '<span class="citation-wrapper" data-url="' + escapeHtml(url) + '">',
      '<a href="' +
        escapeHtml(url) +
        '" target="_blank" rel="noopener noreferrer" class="citation-ref" data-citation="' +
        escapeHtml(citationKey) +
        '">',
      '<sup class="citation-number">' + escapeHtml(citationKey) + '</sup>',
      '</a>',
      '<span class="citation-tooltip">' + escapeHtml(title) + '</span>',
      '</span>',
    ].join('');

    return true;
  });

  // Add a rule to process simple text-based references [^1], [^Note] and [1]
  md.core.ruler.after('inline', 'citation-refs', state => {
    const blockTokens = state.tokens;

    for (let i = 0; i < blockTokens.length; i += 1) {
      if (blockTokens[i].type === 'inline') {
        const inlineTokens = blockTokens[i].children;
        const newTokens = [];

        for (let j = 0; j < inlineTokens.length; j += 1) {
          const token = inlineTokens[j];

          // Only process text tokens (not html_inline which we already created)
          if (token.type === 'text') {
            const text = token.content;
            const allMatches = [];
            let match;

            // Find all numeric citations [1], [2]
            NUMERIC_CITATION_REGEX.lastIndex = 0;
            match = NUMERIC_CITATION_REGEX.exec(text);
            while (match !== null) {
              allMatches.push({
                index: match.index,
                length: match[0].length,
                number: match[1],
                full: match[0],
                type: 'numeric',
              });
              match = NUMERIC_CITATION_REGEX.exec(text);
            }

            // Find all simple references [^1], [^Note], etc. that don't have (url) after them
            SIMPLE_REFERENCE_REGEX.lastIndex = 0;
            match = SIMPLE_REFERENCE_REGEX.exec(text);
            while (match !== null) {
              // Check if this is followed by (url) - if so, skip it (already handled by inline rule)
              const nextChar = text[match.index + match[0].length];
              if (nextChar !== '(') {
                const refText = match[1];
                allMatches.push({
                  index: match.index,
                  length: match[0].length,
                  reference: refText,
                  isNumeric: /^\d+$/.test(refText),
                  full: match[0],
                  type: 'simple',
                });
              }
              match = SIMPLE_REFERENCE_REGEX.exec(text);
            }

            // Sort matches by position
            allMatches.sort((a, b) => a.index - b.index);

            if (allMatches.length === 0) {
              newTokens.push(token);
            } else {
              let lastIndex = 0;

              // Split text and insert citation tokens
              allMatches.forEach(citMatch => {
                // Add text before citation
                if (citMatch.index > lastIndex) {
                  const textToken = new state.Token('text', '', 0);
                  textToken.content = text.substring(lastIndex, citMatch.index);
                  newTokens.push(textToken);
                }

                // Handle different citation types
                if (citMatch.type === 'simple') {
                  // Simple reference [^1] or [^Note]
                  const refText = citMatch.reference;
                  const refKey = citMatch.isNumeric
                    ? refText
                    : refText.toLowerCase();

                  // Check if there's a citation with this key
                  const citation = citations[refKey] || citations[refText];

                  if (citation && citation.url) {
                    // Has URL - render as clickable link with tooltip
                    const tooltipText = citation.title || citation.url;

                    const htmlToken = new state.Token('html_inline', '', 0);
                    htmlToken.content = [
                      '<span class="citation-wrapper" data-url="' +
                        escapeHtml(citation.url) +
                        '">',
                      '<a href="' +
                        escapeHtml(citation.url) +
                        '" target="_blank" rel="noopener noreferrer" class="citation-ref" data-citation="' +
                        escapeHtml(refText) +
                        '">',
                      '<sup class="citation-number">' +
                        escapeHtml(refText) +
                        '</sup>',
                      '</a>',
                      '<span class="citation-tooltip">' +
                        escapeHtml(tooltipText) +
                        '</span>',
                      '</span>',
                    ].join('');
                    newTokens.push(htmlToken);
                  } else {
                    // No URL - render as simple superscript
                    const htmlToken = new state.Token('html_inline', '', 0);
                    htmlToken.content =
                      '<sup class="footnote-ref" data-ref="' +
                      escapeHtml(refText) +
                      '">' +
                      escapeHtml(refText) +
                      '</sup>';
                    newTokens.push(htmlToken);
                  }
                } else if (citMatch.type === 'numeric') {
                  // Numeric citation [1] with citations object
                  const citation = citations[citMatch.number];

                  if (citation && citation.url) {
                    const tooltipText = citation.title || citation.url;

                    const htmlToken = new state.Token('html_inline', '', 0);
                    htmlToken.content = [
                      '<span class="citation-wrapper" data-url="' +
                        escapeHtml(citation.url) +
                        '">',
                      '<a href="' +
                        escapeHtml(citation.url) +
                        '" target="_blank" rel="noopener noreferrer" class="citation-ref" data-citation="' +
                        citMatch.number +
                        '">',
                      '<sup class="citation-number">' +
                        citMatch.number +
                        '</sup>',
                      '</a>',
                      '<span class="citation-tooltip">' +
                        escapeHtml(tooltipText) +
                        '</span>',
                      '</span>',
                    ].join('');
                    newTokens.push(htmlToken);
                  } else {
                    // No citation found, keep original text
                    const textToken = new state.Token('text', '', 0);
                    textToken.content = citMatch.full;
                    newTokens.push(textToken);
                  }
                }

                lastIndex = citMatch.index + citMatch.length;
              });

              // Add remaining text after last citation
              if (lastIndex < text.length) {
                const textToken = new state.Token('text', '', 0);
                textToken.content = text.substring(lastIndex);
                newTokens.push(textToken);
              }
            }
          } else {
            // Keep non-text tokens as is (including html_inline from citation rule)
            newTokens.push(token);
          }
        }

        blockTokens[i].children = newTokens;
      }
    }
  });
}
